/**
 * ShortcutHelp Modal - Shows available keyboard shortcuts (triggered by "?")
 */

import { useEffect } from 'react';

const SHORTCUTS = [
  {
    category: 'Navigation',
    items: [
      { keys: ['Ctrl/Cmd', 'K'], description: 'Open quick search' },
      { keys: ['?'], description: 'Show keyboard shortcuts' },
      { keys: ['G', 'D'], description: 'Go to Dashboard' },
      { keys: ['G', 'E'], description: 'Go to Events' },
      { keys: ['G', 'S'], description: 'Go to My Schedule' },
      { keys: ['G', 'P'], description: 'Go to Profile' },
    ],
  },
  {
    category: 'Lists & Tables',
    items: [
      { keys: ['↑', '↓'], description: 'Navigate items' },
      { keys: ['Enter'], description: 'Select/Open item' },
      { keys: ['Esc'], description: 'Close modal/dialog' },
    ],
  },
  {
    category: 'Actions',
    items: [
      { keys: ['N'], description: 'Create new (when applicable)' },
      { keys: ['E'], description: 'Edit selected item' },
      { keys: ['Delete'], description: 'Delete selected item' },
    ],
  },
];

export default function ShortcutHelp({ isOpen, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <style>{styles}</style>
      <div className="shortcut-overlay" onClick={onClose}>
        <div className="shortcut-modal" onClick={e => e.stopPropagation()}>
          <div className="shortcut-header">
            <h2>Keyboard Shortcuts</h2>
            <button className="shortcut-close" onClick={onClose}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 20, height: 20 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="shortcut-content">
            {SHORTCUTS.map(section => (
              <div key={section.category} className="shortcut-section">
                <h3 className="shortcut-category">{section.category}</h3>
                <div className="shortcut-list">
                  {section.items.map((shortcut, index) => (
                    <div key={index} className="shortcut-item">
                      <span className="shortcut-description">{shortcut.description}</span>
                      <span className="shortcut-keys">
                        {shortcut.keys.map((key, i) => (
                          <span key={i}>
                            <kbd>{key}</kbd>
                            {i < shortcut.keys.length - 1 && <span className="key-separator">+</span>}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="shortcut-footer">
            Press <kbd>?</kbd> anytime to toggle this help
          </div>
        </div>
      </div>
    </>
  );
}

const styles = `
  .shortcut-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: fadeIn 0.15s ease-out;
    padding: 2rem;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .shortcut-modal {
    width: 100%;
    max-width: 500px;
    background: #111111;
    border: 1px solid #2a2a2a;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    animation: slideUp 0.2s ease-out;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .shortcut-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #1f1f1f;
  }

  .shortcut-header h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    font-weight: 400;
    color: #f5f0e8;
    margin: 0;
  }

  .shortcut-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: #6b7280;
    cursor: pointer;
    transition: color 0.2s;
  }

  .shortcut-close:hover {
    color: #f5f0e8;
  }

  .shortcut-content {
    padding: 1rem 1.5rem;
    max-height: 60vh;
    overflow-y: auto;
  }

  .shortcut-section {
    margin-bottom: 1.5rem;
  }

  .shortcut-section:last-child {
    margin-bottom: 0;
  }

  .shortcut-category {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #c9a227;
    margin: 0 0 0.75rem 0;
  }

  .shortcut-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .shortcut-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #1a1a1a;
  }

  .shortcut-item:last-child {
    border-bottom: none;
  }

  .shortcut-description {
    font-size: 0.875rem;
    color: #e8e4dc;
  }

  .shortcut-keys {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .shortcut-keys kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 0.5rem;
    background: #1a1a1a;
    border: 1px solid #2a2a2a;
    border-radius: 4px;
    font-size: 0.6875rem;
    font-family: inherit;
    color: #9ca3af;
    text-transform: uppercase;
  }

  .key-separator {
    color: #4b5563;
    font-size: 0.75rem;
    margin: 0 0.125rem;
  }

  .shortcut-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid #1f1f1f;
    font-size: 0.8125rem;
    color: #6b7280;
    text-align: center;
  }

  .shortcut-footer kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 0.375rem;
    background: #1a1a1a;
    border: 1px solid #2a2a2a;
    border-radius: 3px;
    font-size: 0.625rem;
    font-family: inherit;
    color: #9ca3af;
    margin: 0 0.25rem;
  }
`;
